import React, { useEffect, useState } from 'react';
import './BillingAddonModal.scss';
import ModalWrap from 'Components/ModalWrap';
import {
	Modal,
	ModalHeader,
	ModalContent,
	ModalControls,
	Button,
	ButtonGroup,
	Block,
	Text,
	Column,
	Row
} from '@upsales/components';
import translate from 'Components/Helpers/translate';
import T from 'Components/Helpers/translate';
import { Addon } from './Addon';
import { billingTracker } from 'App/babel/helpers/Tracker';

type BillingAddonCardModalProps = {
	abort: string;
	children: React.ReactNode;
	color: 'orange' | 'red' | 'bright-blue' | 'green';
	onConfirm: (quantity?: number) => void;
	onAbort: () => void;
	loading: boolean;
	yesDisabled: boolean;
	visible: boolean;
	addon: Addon;
};

const BillingAddonCardModal = ({
	addon,
	color,
	loading,
	children,
	onConfirm,
	onAbort,
	visible,
	abort,
	yesDisabled
}: BillingAddonCardModalProps) => {
	const [quantity, setQuantity] = useState(1);
	useEffect(() => {
		if (visible) {
			billingTracker.track(billingTracker.events.OPEN_ADDON_TERMS, { addon: addon.alias });
		}
	}, [visible]);

	const getPeriod = (interval: number) => {
		switch (interval) {
			case 12:
				return 'year';
			case 6:
				return 'halfYear';
			case 3:
				return 'quarter';
			default:
				return 'month';
		}
	};

	const billingPeriod = (addon: Addon) => {
		if (addon.isAgent) {
			return addon.alias.includes('MONTH') ? 'month' : 'year';
		}
		return getPeriod(addon.priceMonthPeriod);
	};
	const getTotalPriceTitle = (addon: Addon) => {
		if (!addon) return;
		if (addon.price === 0) {
			return (
				<Text size="xl" bold>
					{T('integration.free')}
				</Text>
			);
		} else if (addon.unitBased) {
			const totalPrice = addon.pricePerMonth * quantity;
			const period = getPeriod(1);
			return (
				<Text size="xl" color="grey-10">
					<Text size="xl" bold>
						{totalPrice}
					</Text>
					{` ${addon.currency} total per ${period}`}
				</Text>
			);
		} else {
			const price = addon.isAgent ? addon.pricePerMonth * addon.intervalPeriod : addon.totalPricePerMonth;
			const period = addon.isAgent ? getPeriod(addon.intervalPeriod) : getPeriod(1);

			return (
				<Text size="xl" color="grey-10">
					<Text size="xl" bold>
						{price}
					</Text>
					{`${addon.currency} total per ${period}`}
				</Text>
			);
		}
	};
	const getSubtitle = (addon: Addon) => {
		if (!addon || addon.price === 0) {
			return null;
		}
		if (addon.unitBased) {
			return (
				<Text size="md" color="grey-10">
					<Text size="md" bold>
						{addon.pricePerMonth}
					</Text>
					{` ${addon.currency} ${T('admin.billing.perLicense')}`}
				</Text>
			);
		}
		if (addon.basedOn === 'activeUsers' && !addon.tier) {
			return (
				<Text size="md" color="grey-10">
					<Text size="md" bold>
						{addon.pricePerMonth}
					</Text>
					{` ${addon.currency} total per user`}
				</Text>
			);
		}
		if (addon.basedOn === 'activeUsers' && addon.tier) {
			return <Text size="sm" color="grey-10">{`${addon.tier.start} - ${addon.tier.end} users per year`}</Text>;
		}
		if (addon.basedOn !== 'activeUsers' && addon.tier) {
			return (
				<Text size="sm" color="grey-10">{`${addon.tier.start} - ${addon.tier.end} used per ${billingPeriod(
					addon
				)}`}</Text>
			);
		}
	};
	return (
		<ModalWrap isMounted={visible}>
			<Modal id="billing-addon-modal">
				<ModalHeader />
				<ModalContent>
					{children}
					{addon?.unitBased ? (
						<Block border="ts" borderColor="grey-4" space="ptl pbl pll prl">
							<Text bold>{T('admin.billing.chooseQuantity')}</Text>
							<Row align="center">
								<ButtonGroup>
									<Button
										className="ButtonGroup__no-flex"
										color="light-grey"
										size="sm"
										shadow="none"
										disabled={quantity <= 1}
										onClick={() => setQuantity(Math.max(1, quantity - 1))}
									>
										{'-'}
									</Button>
									<Button
										className="ButtonGroup__no-flex"
										color="light-grey"
										size="sm"
										shadow="none"
										onClick={() => setQuantity(quantity + 1)}
									>
										{'+'}
									</Button>
								</ButtonGroup>
								<Text size="sm" color="grey-10" space="mll">
									{quantity === 1
										? `${quantity} ${T('admin.billing.addonLicense')}`
										: `${quantity} ${T('admin.billing.addonLicenses')}`}
								</Text>
							</Row>
						</Block>
					) : null}
				</ModalContent>
				<ModalControls>
					<Block className="priceblock" backgroundColor="grey-1" border="s" borderColor="grey-6">
						<Row className="price-row" direction="row">
							<Column className="price-col">
								{getTotalPriceTitle(addon)}
								{getSubtitle(addon)}
							</Column>
							<Column className="button-col">
								{onConfirm ? (
									<Column>
										<Button
											disabled={yesDisabled}
											loading={loading}
											color={color}
											onClick={() => {
												billingTracker.track(billingTracker.events.BOUGHT_ADDON, {
													addon: addon.alias
												});
												onConfirm(addon.unitBased ? quantity : undefined);
											}}
											size="lg"
										>
											{T('admin.billing.addon.accept_terms_and_add')} {T(addon.name)}
										</Button>
									</Column>
								) : null}
								{onAbort ? (
									<Column>
										<Button
											disabled={loading}
											color="grey"
											type="link"
											size="lg"
											onClick={() => {
												billingTracker.track(billingTracker.events.ABORTED_ADDON_PURCHASE, {
													addon: addon.alias
												});
												onAbort();
											}}
										>
											{translate(abort)}
										</Button>
									</Column>
								) : null}
							</Column>
						</Row>
					</Block>
				</ModalControls>
			</Modal>
		</ModalWrap>
	);
};

BillingAddonCardModal.defaultProps = {
	color: 'orange',
	abort: 'default.abort',
	yesDisabled: false,
	loading: false
};

export default BillingAddonCardModal;
